import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import classNames from "classnames";
import Text from "./Text";

/**
 * Reusable section header used across HomeV2.
 *
 * Renders: optional eyebrow chip (dot + label) + two-tone title
 * (muted half in grey, bright half in solid black/white) + optional
 * subtitle + optional CTA row.
 *
 * Every piece of copy uses the shared `<Text>` primitive — no inline
 * `font-inter` / `font-sans` / `text-[Npx]` classes — so typography
 * changes happen in one place.
 */

export type SectionHeaderVariant =
  | "dark-lime"
  | "light-teal"
  | "dark-teal"
  | "light-magenta"
  | "light-olive";

export type SectionHeaderAlign = "center" | "left";

export type SectionHeaderProps = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  variant?: SectionHeaderVariant;
  align?: SectionHeaderAlign;
  className?: string;
};

type VariantSpec = {
  eyebrowFg: string;
  eyebrowDotBg: string;
  brightBase: string;
  mutedBase: string;
  subtitle: string;
};

const variantSpec: Record<SectionHeaderVariant, VariantSpec> = {
  "dark-lime": {
    eyebrowFg: "text-brandSecondary",
    eyebrowDotBg: "bg-brandSecondary",
    // Per design direction: both halves of the title are solid — white
    // on dark sections, near-black on light. No muted grey split.
    brightBase: "text-white",
    mutedBase: "text-white",
    subtitle: "text-darkSlate03",
  },
  "light-teal": {
    eyebrowFg: "text-[#007F8C]",
    eyebrowDotBg: "bg-[#007F8C]",
    brightBase: "text-neutralBlack",
    mutedBase: "text-neutralBlack",
    subtitle: "text-darkSlate04",
  },
  "dark-teal": {
    eyebrowFg: "text-[#5BD6C5]",
    eyebrowDotBg: "bg-[#5BD6C5]",
    brightBase: "text-white",
    mutedBase: "text-white",
    subtitle: "text-darkSlate03",
  },
  "light-magenta": {
    eyebrowFg: "text-[#CA26A5]",
    eyebrowDotBg: "bg-[#CA26A5]",
    brightBase: "text-neutralBlack",
    mutedBase: "text-neutralBlack",
    subtitle: "text-darkSlate04",
  },
  "light-olive": {
    eyebrowFg: "text-[#4F7F06]",
    eyebrowDotBg: "bg-[#4F7F06]",
    brightBase: "text-neutralBlack",
    mutedBase: "text-neutralBlack",
    subtitle: "text-darkSlate04",
  },
};

const SectionHeader = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  variant = "light-teal",
  align = "center",
  className,
}: SectionHeaderProps) => {
  const spec = variantSpec[variant];
  const alignmentClass = align === "center" ? "text-center" : "text-left";
  const flexJustifyClass =
    align === "center" ? "justify-center" : "justify-start";
  return (
    <div className={classNames("w-full", className)}>
      {eyebrow ? (
        <div className={classNames("flex items-center gap-2", flexJustifyClass)}>
          <span
            className={classNames(
              "inline-block h-1.5 w-1.5 rounded-full",
              spec.eyebrowDotBg
            )}
          />
          <Text
            variant="eyebrow"
            className={spec.eyebrowFg}
            style={{ letterSpacing: "0.6px" }}
          >
            {eyebrow}
          </Text>
        </div>
      ) : null}

      <Text
        as="h2"
        variant="display-lg"
        className={classNames(
          // Responsive ramp: mobile 28, small 32, tablet 40, desktop 48.
          // The extra 28px step prevents section titles from overflowing
          // their container on iPhone SE-class viewports.
          "mt-3 text-[24px] sm:text-[24px] md:mt-4 md:text-[40px] lg:text-[48px]",
          alignmentClass,
          align === "center" ? "mx-auto max-w-4xl" : ""
        )}
      >
        {titleMuted ? (
          <span className={spec.mutedBase}>{titleMuted}</span>
        ) : null}
        {titleMuted && titleBright ? <br /> : null}
        {titleBright ? (
          <span className={spec.brightBase}>{titleBright}</span>
        ) : null}
        {!titleMuted && !titleBright && title ? (
          <span className={spec.brightBase}>{title}</span>
        ) : null}
      </Text>

      {subtitle ? (
        <Text
          as="p"
          variant="body-lg"
          className={classNames(
            "mt-5",
            alignmentClass,
            align === "center" ? "mx-auto max-w-2xl" : "max-w-2xl",
            spec.subtitle
          )}
          style={{ letterSpacing: "-0.08px" }}
        >
          {subtitle}
        </Text>
      ) : null}

      {ctas && ctas.length ? (
        <div
          className={classNames(
            "mt-8 flex flex-wrap items-center gap-3",
            flexJustifyClass
          )}
        >
          <CtaButtonGroup ctas={ctas} gap="flex gap-3" fullWidthOnMobile={false} />
        </div>
      ) : null}
    </div>
  );
};

export default SectionHeader;
